import React, { useEffect, useState, useRef } from 'react';
import AOS from 'aos';
import { useNavigate } from 'react-router-dom';
import 'aos/dist/aos.css';
import './home.css';
import EditableText from '../../components/editable/EditableText';
import EditableImage from '../../components/editable/EditableImage';
import EditableBackground from '../../components/editable/EditableBackground';
import EditableHeroSlideshow from '../../components/editable/EditableHeroSlideshow';
import EditableHomeSermons from '../../components/editable/EditableHomeSermons';
import EditableServiceTimes from '../../components/editable/EditableServiceTimes';
import EditableVerseLine from '../../components/editable/EditableVerseLine';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import { DEFAULT_HERO_SLIDES, resolveHeroSlides } from '../../data/homeHeroSlides';
import { resolveHomeVerses } from '../../data/homeVerses';

import aboutImage from '../../assets/home/abouts.png';
import pastorImage from '../../assets/home/pastorpic.png';
import aboutBackground from '../../assets/home/aboutBackground.png';
import versesBackground from '../../assets/home/versesbackground.mp4';
import ChurchCalendarSection from '../../components/churchCalendar/ChurchCalendarSection';

const Home = () => {
  const [step, setStep] = useState(0);
  const [heroSlide, setHeroSlide] = useState(0);
  const { isEditing } = useAuth();
  const { getContent } = useContent();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const verses = resolveHomeVerses(getContent);
  const heroSlides = resolveHeroSlides(getContent);

  const storedHeroSlides = getContent('home.hero.slides', null);
  const heroSlideCount = Array.isArray(storedHeroSlides) ? storedHeroSlides.length : heroSlides.length;

  const MARQUEE_ITEMS = [
    'Moments of Grace',
    "God's Grace",
    'Faith',
    'Worship',
    'Peace',
    'Hope',
    'Love',
    'Praise',
  ];

  useEffect(() => {
    window.scrollTo(0, 0);

    AOS.init({
      duration: 600,
      offset: 60,
      once: false,
      mirror: true,
      easing: 'ease-out-cubic',
      anchorPlacement: 'top-bottom',
      delay: 0,
    });

    // Verse cycling logic
    const interval = setInterval(() => {
      setStep((prevStep) => (prevStep + 1) % verses.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [verses.length]);

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReducedMotion || isEditing) return undefined;

    const interval = setInterval(() => {
      setHeroSlide((prev) => (prev + 1) % heroSlideCount);
    }, 6000);

    return () => clearInterval(interval);
  }, [isEditing, heroSlideCount]);

  useEffect(() => {
    setHeroSlide((prev) => (prev >= heroSlideCount ? 0 : prev));
  }, [heroSlideCount]);

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      document.querySelectorAll('.main-title').forEach((el) => {
        el.classList.add('fade-in');
      });
    }, 100);
    return () => clearTimeout(fadeTimer);
  }, []);

  return (
    <>
      {/* Home Section */}
      <div ref={heroRef} className="home-container">
        <EditableHeroSlideshow
          contentKey="home.hero.slides"
          defaultSlides={DEFAULT_HERO_SLIDES}
          activeIndex={heroSlide}
        />
        <div className="background-overlay">
          {/* Subtle dot grid – static layer */}
          <div
            className="hero-dot-grid"
            style={{
              backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
            aria-hidden="true"
          />
          {/* Animated dot grid – moves on page load */}
          <div
            className="hero-dot-grid hero-dot-grid--animated"
            style={{
              backgroundImage: 'radial-gradient(circle at 50% 50%, white 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
            aria-hidden="true"
          />
          <div className="welcome-content">
            <EditableText contentKey="home.hero.welcome" tag="p" className="hero-welcome-line">
              Welcome to
            </EditableText>
            <div className="church-name-container">
              <div className="church-title-wrapper">
                <EditableText contentKey="home.hero.name_main" tag="h1" className="church-name-main">
                  PHILADELPHIA SHALEM
                </EditableText>
                <EditableText contentKey="home.hero.name_secondary" tag="h1" className="church-name-secondary">
                  ASSEMBLY OF GOD
                </EditableText>
                <div className="hero-title-underline" aria-hidden="true" />
              </div>
            </div>
            <EditableText
              contentKey="home.hero.description"
              tag="p"
              className="church-description"
              html
              multiline
            >
              A place where creation meets the Creator<br />
              for an intimate relationship with Him
            </EditableText>
            <div className="action-buttons">
              <a 
                href="https://www.youtube.com/@psagchurch" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="action-button"
                style={{ textDecoration: 'none' }}
              >
                WATCH LIVE
              </a>
              <a 
                href="/visit-us" 
                className="action-button"
                style={{ textDecoration: 'none' }}
              >
                PLAN YOUR VISIT
              </a>
            </div>
            <a href="#about-psag" className="hero-scroll-hint" aria-label="Scroll to content">
              <span className="hero-scroll-hint-line" />
            </a>
          </div>
        </div>
      </div>

      {/* Marquee – scrolling phrases */}
      <section className="home-marquee-section" aria-hidden="true">
        <div className="home-marquee-gradient" />
        <div className="home-marquee-wrap">
          <div className="home-marquee-track">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
              <EditableText
                key={i}
                contentKey={`home.marquee.${i % MARQUEE_ITEMS.length}`}
                tag="span"
                className="home-marquee-item"
              >
                {item}
              </EditableText>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <div id="about-psag" className="about-section scroll-reveal-section" data-aos="fade-up">
        <div className="about-content">
          <EditableText contentKey="home.about.title" tag="h2" className="about-title" data-aos="fade-down" data-aos-duration="600">
            ABOUT PSAG CHURCH
          </EditableText>
          <div className="header-line" data-aos="fade-up" data-aos-delay="100" data-aos-duration="500"></div>
          <EditableText contentKey="home.about.p1" tag="p" className="description" multiline data-aos="fade-up" data-aos-delay="150">
            Welcome to PSAG Church, where the name of Jesus is lifted above
            every name! We are a Christ-centered community dedicated to sharing
            God's love, offering hope, and building faith through fellowship,
            worship, and service. Here, you'll find a place to belong, grow, and
            experience the peace and joy that comes from knowing Jesus. Welcome to our Malayalam Pentecostal Church in Philadelphia, serving the Indian Christian community with Spirit-filled worship, powerful sermons, and Malayalam services. If you're searching for a Malayalam church in Pennsylvania or a Pentecostal fellowship for the Malayalam-speaking community in America, we invite you to join our growing family in Christ.
          </EditableText>
          <EditableText contentKey="home.about.p2" tag="p" className="description" multiline data-aos="fade-up" data-aos-delay="200">
            Whether you're new to faith or looking for a deeper connection, we
            invite you to join us. Together, let's celebrate God's grace and
            create a space where everyone feels loved, valued, and inspired to
            grow in their relationship with Christ.
          </EditableText>
          <button className="learn-more-button" data-aos="fade-up" data-aos-delay="250" onClick={() => navigate('/story')}>
            LEARN MORE
          </button>
          <div className="about-image" data-aos="fade-left" data-aos-delay="200">
            <EditableImage
              contentKey="home.about.image"
              src={aboutImage}
              alt="About PSAG Church"
              className="clickable-image"
            />
          </div>
        </div>
      </div>

      {/* Bible Verse Section */}
      <div className="bible-verse-section">
        <span
          className="editor-field-anchor"
          data-content-key="home.verses.items"
          data-verse-count={verses.length}
          aria-hidden="true"
        />
        <video className="background-video" autoPlay loop muted playsInline src={versesBackground}></video>
        <div className="verse-container">
          {verses.map((verse, index) => (
            <div
              key={index}
              className={`bible-verse ${step === index ? 'verse-active' : 'verse-hidden'}`}
            >
              <span className="verse-text">
                <EditableVerseLine index={index} field="text" tag="span" />
              </span>
              <br />
              <span className="verse-reference">
                - <EditableVerseLine index={index} field="ref" tag="span" />
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Pastor Section */}
      <EditableBackground
        className="our-pastor-section scroll-reveal-section"
        contentKey="home.pastor.background"
        src={aboutBackground}
        data-aos="fade-up"
      >
        <div className="pastor-content">
          <div className="pastor-details" data-aos="fade-right" data-aos-delay="100">
            <EditableText contentKey="home.pastor.title" tag="h2" className="pastor-title">
              MEET OUR PASTOR
            </EditableText>
            <div className="pastor-title-divider"></div>
            <EditableText contentKey="home.pastor.p1" tag="p" className="pastor-description" multiline>
              I am Pr. Sajan George, blessed to serve as the founder and pastor of our
              vibrant and faith-filled community here in the heart of Philadelphia, PA.
              Since our doors opened in 2014, we have been dedicated to building a sanctuary
              where lives are transformed, faith is nurtured, and hearts are united in worship.
            </EditableText>
            <EditableText contentKey="home.pastor.p2" tag="p" className="pastor-description" multiline>
              It would be my joy to personally invite you and your loved ones to join us for one of our
              upcoming services this weekend. At PSAG Church, we cherish the power of fellowship,
              the beauty of community, and the hope that faith brings. Come as you are, and let's grow
              together in God's love and grace. We can't wait to welcome you into our family!
            </EditableText>
          </div>
          <div className="pastor-image" data-aos="fade-left" data-aos-delay="150">
            <EditableImage
              contentKey="home.pastor.image"
              src={pastorImage}
              alt="Pr. Sajan George"
            />
            <div className="pastor-caption">
              <EditableText contentKey="home.pastor.name" tag="div" className="pastor-name">
                Pr. Sajan George
              </EditableText>
              <a href="tel:+12679843132" className="pastor-phone">(267) 984-3132</a>
            </div>
          </div>
        </div>
      </EditableBackground>

      {/* Sermons Section */}
      <div className="youtube-videos-section scroll-reveal-section" data-aos="fade-up">
        <div className="section-header">
          <EditableText contentKey="home.sermons.title" tag="h2" className="section-title" data-aos="fade-down" data-aos-duration="600">
            Sermons
          </EditableText>
          <div className="divider" data-aos="fade-up" data-aos-delay="80"></div>
        </div>
        <EditableHomeSermons />
      </div>

      {/* Service Times Section */}
      <div className="service-times-section scroll-reveal-section" data-aos="fade-up" data-aos-duration="700" data-aos-offset="80">
        <h2 className="section-title" data-aos="fade-down" data-aos-duration="600" data-aos-offset="80">
          <EditableText contentKey="home.services.title" tag="span">
            Service Times
          </EditableText>
          <div className="divider" data-aos="fade-up" data-aos-delay="100" data-aos-duration="500"></div>
        </h2>
        <EditableServiceTimes />
      </div>

      {/* Church Calendar + Upcoming Events */}
      <ChurchCalendarSection
        className="scroll-reveal-section"
        titleClassName="section-title"
        sectionProps={{
          'data-aos': 'fade-up',
          'data-aos-duration': '700',
          'data-aos-offset': '80',
        }}
      />

      {/* Visitor Forum Section */}
      <section className="visitor-forum-section scroll-reveal-section fade-in" data-aos="fade-up" data-aos-duration="700" data-aos-offset="80">
        <EditableText contentKey="home.join.title" tag="h2" className="section-title" data-aos="fade-down" data-aos-duration="600" data-aos-offset="80">
          Join Us
        </EditableText>
        <div className="divider" data-aos="fade-up" data-aos-delay="100" data-aos-duration="500"></div>
        <EditableText contentKey="home.join.description" tag="p" className="visitor-forum-description" multiline data-aos="fade-up" data-aos-delay="120" data-aos-duration="600">
          Ready to connect with our church family? Fill out our Visitor Forum to let us know you're coming, share your details, and help us welcome you warmly. We are excited to meet you and grow together in faith!
        </EditableText>
        <a
          href="https://docs.google.com/forms/d/e/1FAIpQLSfQqpsfZ-hQKKeykQ7-2JD2pyGYApycuW9oLsUi_39LlDajfA/viewform?usp=dialog"
          target="_blank"
          rel="noopener noreferrer"
          className="view-more-button"
          data-aos="fade-up"
          data-aos-delay="180"
          data-aos-duration="600"
        >
          Visit Forum
        </a>
      </section>
    </>
  );
};

export default Home;
